import {useState, useCallback} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography} from '@mui/material'
import type {Blocker} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {store} from '../../common/store'
import {useEditorConfig} from '../../common/hooks/useEditorConfig'
import {serializeConfig} from './topMenuBar/serializeConfig'
import {setSavedSnapshot} from '../../common/components/store/savedSnapshotStore'
import {createMyProject, updateMyProject} from '../../common/api/generated/endpoints/my-projects/my-projects'
import {stableStringify} from '../../common/utils/stableStringify'
import LoadingLogo from './LoadingLogo'

interface LeaveGuardModalProps {
  blocker: Blocker
}

const LeaveGuardModal = ({blocker}: LeaveGuardModalProps) => {
  const {t} = useTranslation()
  const {projectId} = useParams()
  const navigate = useNavigate()
  const {deviceConfig} = useEditorConfig()
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [projectName, setProjectName] = useState('')

  const isNewProject = !projectId || projectId === 'new'
  const open = blocker.state === 'blocked'

  const handleCancel = useCallback(() => {
    blocker.reset?.()
    setProjectName('')
    setSaveError(null)
  }, [blocker])

  const handleLeaveWithoutSaving = useCallback(() => {
    blocker.proceed?.()
  }, [blocker])

  const handleSaveAndLeave = useCallback(async () => {
    setSaving(true)
    try {
      const state = store.getState().present
      const merged = serializeConfig(state, deviceConfig)
      const scenesPayload = merged.scenes.map((s, idx) => ({
        name: `Scene ${idx + 1}`,
        config: s as unknown as Record<string, unknown>,
      }))

      if (projectId && projectId !== 'new') {
        await updateMyProject(projectId, {scenes: scenesPayload})
      } else {
        const {data} = await createMyProject({
          name: projectName.trim() || 'Untitled Project',
          scenes: scenesPayload,
        })
        // Navigate to the new project URL so the user lands on the correct page later
        navigate(`/editor/${data.id}`, {replace: true})
      }

      setSavedSnapshot(stableStringify(merged))
      blocker.proceed?.()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : t('toolbar.saveFailed'))
    } finally {
      setSaving(false)
    }
  }, [blocker, deviceConfig, projectId, projectName, navigate, t])

  return (
    <Dialog open={open} onClose={handleCancel}>
      <DialogTitle sx={sx.title}>{t('toolbar.leaveTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{mb: isNewProject || saveError ? 2 : 0}}>
          {t('toolbar.leaveMessage')}
        </Typography>
        {saveError && (
          <Typography variant="body2" color="error" sx={{mb: isNewProject ? 2 : 0}}>
            {saveError}
          </Typography>
        )}
        {isNewProject && (
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={t('toolbar.projectNamePlaceholder')}
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && projectName.trim()) handleSaveAndLeave()
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>{t('toolbar.cancel')}</Button>
        <Button onClick={handleLeaveWithoutSaving} color="error">
          {t('toolbar.leaveWithoutSaving')}
        </Button>
        <Button
          onClick={handleSaveAndLeave}
          variant="contained"
          disabled={saving || (isNewProject && !projectName.trim())}
        >
          {saving ? <LoadingLogo size={16} /> : t('toolbar.saveAndLeave')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const sx = {
  title: {
    fontSize: 16,
    fontWeight: 600,
  },
}

export default LeaveGuardModal
